import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { AddressComponent } from './components/address/address.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CustomerService } from '../../features/customers/services/customer.service';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import {
  customEmailValidator,
  noWhitespaceValidator,
} from '../../shared/services/validations';
import { CustomerDocumentsComponent } from './components/documents/customer-documents.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { GenderOptionsEnum } from '../../../assets/enum/genderOptionsEnum';
import { preferenceOptionsEnum } from '../../../assets/enum/preferenceTypesEnum';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { LeadService } from '../../features/leads/services/lead.service';

@Component({
  selector: 'app-client-new',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    AddressComponent,
    ReactiveFormsModule,
    FormsModule,
    CustomerDocumentsComponent,
    MatDatepickerModule,
    MatCheckboxModule,
  ],
  templateUrl: './client-new.component.html',
  styleUrl: './client-new.component.scss',
})
export class ClientNewComponent {
  @ViewChild(AddressComponent) addressComponent!: AddressComponent;
  @ViewChild(CustomerDocumentsComponent)
  customerDocumentsComponent!: CustomerDocumentsComponent;

  newCustomerForm: FormGroup;
  customerData: any = {};
  leadCustomerData: any = {};
  addressDetails: any;
  contactDetails: any = [];
  contactPersons: any = [];
  remarkDetails: any;
  documentDetails: any = {};
  fileName: any;
  contacts: any;
  customerId: any;
  validationError: boolean = false;
  documentArray: any[] = [];
  public genderData: any[] = [];
  public preferenceData: any[] = [];
  today: Date = new Date();
  maxDate: Date;
  isSameAsPhone = false;
  leadId: any;
  leadStatus: boolean = false;

  constructor(
    private fb: FormBuilder,
    private customerService: CustomerService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private leadService: LeadService
  ) {
    const today = new Date();
    this.maxDate = new Date(
      today.getFullYear() - 10,
      today.getMonth(),
      today.getDate()
    );
  }

  ngOnInit() {
    this.initForm();
    this.loadGender();
    this.loadPreferences();
    this.route.paramMap.subscribe((params) => {
      this.customerId = params.get('id');
      if (this.customerId) {
        this.fetchCustomerById();
      }
    });

    this.route.queryParamMap.subscribe((params) => {
      this.leadId = params.get('leadId');
      if (this.leadId) {
        this.fetchLeadById();
      }
    });
  }

  initForm() {
    if (this.customerData || this.leadCustomerData) {
      const preferredChannelsRaw =
        this.customerData?.preferences?.preferredChannels || [];
      const validPreferenceValues = Object.values(preferenceOptionsEnum).map(
        (opt) => opt.value
      );

      const mappedPreferences = preferredChannelsRaw.filter((channel: string) =>
        validPreferenceValues.includes(channel)
      );
      const dob = this.customerData.dateOfBirth
        ? new Date(Number(this.customerData.dateOfBirth))
        : null;

      this.newCustomerForm = this.fb.group({
        id: Number(this.customerId),
        firstName: [
          this.customerData?.firstName || this.leadCustomerData?.leadName || '',
          [
            Validators.required,
            Validators.maxLength(50),
            noWhitespaceValidator(),
          ],
        ],
        lastName: [
          this.customerData?.lastName || '',
          [
            Validators.required,
            Validators.maxLength(50),
            noWhitespaceValidator(),
          ],
        ],
        email: [
          this.customerData.email || this.leadCustomerData?.email || '',
          [Validators.required, customEmailValidator()],
        ],
        phone: [
          this.customerData.phoneNumber || this.leadCustomerData?.phone || '',
          [
            Validators.required,
            Validators.minLength(8), // Minimum length for phone number
            Validators.maxLength(15), // Maximum length for phone number
            Validators.pattern('^[0-9]+$'), // Only allows numeric values
          ],
        ],
        whatsappNo: [
          this.customerData.phoneNumber || '',
          [
            Validators.required,
            Validators.minLength(8), // Minimum length for phone number
            Validators.maxLength(18), // Maximum length for phone number
            Validators.pattern('^\\+?[0-9]+$'),
          ],
        ],
        dateOfBirth: [dob, Validators.required],
        gender: [this.customerData.gender, Validators.required],
        preference: [mappedPreferences, Validators.required],
      });
    } else {
      this.newCustomerForm = this.fb.group({
        firstName: [
          '',
          [
            Validators.required,
            Validators.maxLength(50),
            noWhitespaceValidator(),
          ],
        ],
        lastName: [
          '',
          [
            Validators.required,
            Validators.maxLength(50),
            noWhitespaceValidator(),
          ],
        ],
        email: [
          '',
          [
            Validators.required,
            customEmailValidator(),
            Validators.maxLength(50),
          ],
        ],
        phone: [
          '',
          [
            Validators.required,
            Validators.minLength(8), // Minimum length for phone number
            Validators.maxLength(15), // Maximum length for phone number
            Validators.pattern('^[0-9]+$'), // Only allows numeric values
          ],
        ],
        whatsappNo: [
          '',
          [
            Validators.required,
            Validators.minLength(8), // Minimum length for phone number
            Validators.maxLength(18), // Maximum length for phone number
            Validators.pattern('^\\+?[0-9]+$'),
          ],
        ],
        dateOfBirth: [null, Validators.required],
        gender: [null, Validators.required],
        preference: [[], Validators.required],
      });
    }
  }

  private loadGender() {
    this.genderData = Object.values(GenderOptionsEnum).map((type) => ({
      label: type.label,
      value: type.value,
    }));
  }

  private loadPreferences() {
    this.preferenceData = Object.values(preferenceOptionsEnum).map((type) => ({
      label: type.label,
      value: type.value,
    }));
  }

  addressData: any;
  documentData: any;

  collectAddress(data: any) {
    this.addressData = data;
  }

  collectDocumentDetails(data: any) {
    this.documentData = data;
  }

  fetchCustomerById() {
    this.customerService.fetchCustomerById(Number(this.customerId)).subscribe({
      next: (data) => {
        this.customerData = data;
        this.initForm();
      },
      error: (error) => {
        console.error(error, 'Error');
      },
    });
  }

  fetchLeadById(): void {
    this.customerService.fetchLeadById(Number(this.leadId)).subscribe({
      next: (data) => {
        this.leadStatus = true;
        this.leadCustomerData = data;
        this.initForm();
      },
      error: (error) => console.error(error),
    });
  }

  toggleSameAsPhone(event: any) {
    this.isSameAsPhone = event.checked;

    if (this.isSameAsPhone) {
      const phoneValue = this.newCustomerForm.get('phone')?.value;
      this.newCustomerForm.get('whatsappNo')?.setValue(phoneValue);

      this.newCustomerForm
        .get('phone')
        ?.valueChanges.subscribe((val: string) => {
          if (this.isSameAsPhone) {
            this.newCustomerForm.get('whatsappNo')?.setValue(val);
          }
        });

      // this.newCustomerForm.get('whatsappNo')?.disable();
    } else {
      this.newCustomerForm.get('whatsappNo')?.enable();
      this.newCustomerForm.get('whatsappNo')?.reset();
    }
  }

  async onSubmit() {
    if (this.newCustomerForm.invalid) {
      this.newCustomerForm.markAllAsTouched();
      return;
    }

    const isAddressValid = this.addressComponent.onSubmitBilling();
    this.customerDocumentsComponent.onSubmit();

    if (!isAddressValid) {
      return;
    }

    const formValue = this.newCustomerForm.value;
    const branchId = localStorage.getItem('selectedBranchId');

    const createCustomerInput = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      phoneNumber: formValue.phone,
      gender: formValue.gender,
      dateOfBirth: formValue.dateOfBirth,
      preferences: {
        preferredChannels: formValue.preference,
      },
      ...this.addressData,
      branchId: Number(branchId),
    };

    const updateCustomerInput = {
      id: Number(this.customerId),
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      phoneNumber: formValue.phone,
      gender: formValue.gender,
      dateOfBirth: formValue.dateOfBirth,
      preferences: {
        preferredChannels: formValue.preference,
      },
      ...this.addressData,
      branchId: Number(branchId),
    };
    const customerDocs = this.documentData;

    console.log('Final Payload:', { createCustomerInput, customerDocs });

    if (this.customerId) {
      this.customerService
        .updateCustomer(
          updateCustomerInput,
          customerDocs,
          Number(this.customerId)
        )
        .subscribe({
          next: async (data) => {
            this.toastr.success('Customer updated successfully', 'Success');
            this.onCancel();
            this.router.navigate([`/customers`]);
          },
          error: (error) => this.toastr.error(error, 'Error'),
        });
    } else {
      this.customerService
        .createCustomer(createCustomerInput, customerDocs)
        .subscribe({
          next: async (data) => {
            this.toastr.success('Customer created successfully', 'Success');
            if (this.leadStatus) {
              const customerId = data?.id;
              this.updateLead(customerId);
            } else {
              this.onCancel();
              this.router.navigate([`/customers`]);
            }
          },
          error: (error) => this.toastr.error(error, 'Error'),
        });
    }
  }

  updateLead(customerId: any) {
    const updateLeadsInput = {
      id: Number(customerId),
    };
    this.leadService.updateLead(Number(this.leadId), updateLeadsInput).subscribe({
      next: (res) => {
        this.toastr.success('Lead updated');
        this.onCancel();
        this.router.navigate([`/customers`]);
      },
      error: (err)=>{
        console.log(err);
      }
    });
  }

  onCancel() {
    this.router.navigate([`/customers`]);
  }
}
